/**
 * AcDbAssocArray graph WRITE-BACK — AW-1 "params write-back"
 * (docs/MASTERPLAN.md AW.2). Surgically patches the §9.4 8-field write-back
 * matrix (columnCount/rowCount/levelCount/columnSpacing/rowSpacing/
 * levelSpacing/axisAngle/rowElevation) into a document's PRESERVED,
 * byte-verbatim OBJECTS group stream — the SAME `document.rawObjectGroups`
 * flat array `dxfBridge.export.ts` already uses as the sole export byte
 * source for the AcDbAssocArray graph (AW-0, `dxfBridge.arrayAssocExport.ts`
 * module doc: "keeps the flatten as the sole byte source"). This module
 * deliberately does NOT route through the typed carrier's writer channel
 * (`objectsSection.assocArrays` / `serializeAssocArrayObject`) — doing so
 * reordered the OBJECTS section relative to the entity's own ext-dict
 * DICTIONARY (@codex round-1 P2, see the AW-0 module doc) — it patches VALUE
 * strings in place, at their EXISTING positions, so every untouched group
 * (including ones this module does not understand — GUID payloads, item
 * records, the trailing base-transform matrix) survives byte-for-byte.
 *
 * Real AutoCAD persists the rectangular counts/spacings/axis/elevation as
 * NAMED VALUE PARAMS on the owning `ACDBASSOCACTION` record ("Items" /
 * "Rows" / "Levels" / "ItemSpacing" / "RowSpacing" / "LevelSpacing" /
 * "RowElevation" / "AxesAngle" — `RECT_NAMED_VALUE_PARAMS` in
 * `handleResolution.ts`, the single source of truth for names/fields/codes
 * shared with the import-side decode). A synthetic / ODA-sample producer may
 * instead (or additionally) emit a standalone
 * `ACDBASSOCARRAYRECTANGULARPARAMETERS` record carrying the SAME 8 values
 * POSITIONALLY (canonical order: `assocArrayWriter.ts#appendRectParams`).
 * `patchAssocArrayGraphRawGroups` patches WHICHEVER form(s) the graph
 * actually carries for each edited field (docs/MASTERPLAN.md AW-1: "Write-back
 * обязан патчить ОБЕ формы эмиссии").
 *
 * FORMULA-CHAIN GUARD (AW-1 п.г): a named value param whose block declares
 * `numValues !== 1` (multiple EvalVariants — AutoCAD's on-disk shape for an
 * expression-driven parameter) is NEVER overwritten — the field is
 * expression-governed and write-back declines rather than corrupting the
 * formula chain (AutoCAD parity: an expression-driven cell is not a literal
 * value slot). By construction this rarely fires in practice: the import-side
 * merge (`mergeNamedRectValueParams`, handleResolution.ts) already refuses to
 * populate `assocParamsSnapshot` for a multi-value param, so the AW-0
 * dirty-diff choke point (`dxfBridge.arrayAssocExport.ts`) never proposes an
 * edit for an expression-governed field in the first place — this module's
 * own check is defense-in-depth for any FUTURE caller that does not route
 * through that snapshot gate.
 *
 * KNOWN, DOCUMENTED DEFERRAL — the ACDBASSOCACTION status word: AutoCAD
 * tracks per-action re-evaluation need via an `AcDbAssocStatus` enum
 * (`kIsUpToDateAssocStatus = 0`, `kChangedDirectlyAssocStatus = 1`, …
 * verified against the public ObjectARX SDK headers mirror —
 * github.com/kevinzhwl/ObjectARXCore, `2015/inc/AcDbAssocGlobal.h`, the SAME
 * source already cited in `dxfBridge.arrayAssocImport.ts` for the
 * rectangular-parameters constructor defaults). Bumping this status after a
 * patch would let AutoCAD know to re-evaluate the array on open. HOWEVER the
 * exact DXF GROUP POSITION that carries this status word on the
 * `ACDBASSOCACTION` record's wire format is NOT verified against a primary
 * source (ODA's `OdDbImpAssocAction::dxfInFields` — the actual DXF filer — is
 * NOT part of the public ARX SDK headers; only the real-fixture-observed
 * layout `90 <version> → 90 <unk>` in `decodeAssocActionValueParams`'s doc
 * comment is available, and "unk" is HONESTLY unresearched, not a documented
 * fact). Per CLAUDE.md AW-1 ("НЕ выдумывать") this module does NOT guess at
 * that byte position — patching the WRONG "90" slot would silently corrupt
 * an unrelated action field (dependency count, owned-param count, …), which
 * is strictly worse than leaving the status word untouched. The persisted
 * VALUE this module writes is always correct; only the (separate, additive)
 * "please re-evaluate on open" signal is deferred, tracked as a documented
 * follow-up for a future AW slice with real byte-position verification
 * (e.g. against a real AutoCAD-edited-and-resaved ARRAYRECT fixture).
 */
import type { DxfGroup } from "./types";
import {
  RECT_NAMED_VALUE_PARAMS,
  axesAngleModelDegreesToRadians,
  type RectNamedValueParamEntry,
  type AssocArrayFieldOwnerMap,
} from "./handleResolution";
import { parseStrictInt } from "./assocArrayParser";
import { normalizeHandle } from "./handleNormalize";
import { formatDxfNumber } from "./writer";

/** The 8-field write-back matrix's field-name union — derived from the same
 * table `mergeNamedRectValueParams` (import side) uses, so the two
 * directions can never name a field the other side does not know about. */
export type AssocArrayRectFieldName = RectNamedValueParamEntry["field"];

/** One requested field edit — the entity's CURRENT value (post-dirty-diff),
 * in MODEL units (degrees for `axisAngle`, native drawing units / plain
 * integers otherwise). */
export interface AssocArrayFieldEdit {
  field: AssocArrayRectFieldName;
  value: number;
}

/** Why a specific emission form did NOT receive a field's new value. */
export type AssocArrayPatchSkipReason =
  /** The form does not exist for this array (no target handle), the target
   * handle was not found in the raw stream, or the record does not carry a
   * slot for this field at all (the positional form's positional index was
   * never persisted by the producer). */
  | "missing"
  /** The named-value-param block declares more than one EvalVariant — an
   * expression/formula chain. Write-back declines (AW-1 п.г). */
  | "expression-guarded"
  /** The block's grammar deviated from the expected shape (missing unit/count
   * header) or the value's group code did not match the documented kind
   * (int-90 for counts, real-40 for spacings/angle/elevation). */
  | "unexpected-code";

export interface AssocArrayFieldPatchOutcome {
  field: AssocArrayRectFieldName;
  namedFormApplied: boolean;
  positionalFormApplied: boolean;
  namedFormSkipReason?: AssocArrayPatchSkipReason;
  positionalFormSkipReason?: AssocArrayPatchSkipReason;
}

export interface AssocArrayGraphPatchTarget {
  /** Handle of the owning ACDBASSOCACTION record (named-value-param form). */
  actionHandle?: string;
  /** Handle of the standalone ACDBASSOCARRAYRECTANGULARPARAMETERS record
   * (positional/synthetic form). */
  arrayParametersHandle?: string;
  /**
   * @codex P2 ("Preserve positional-axis units in hybrid arrays") — whether
   * the CALLER's CURRENT `axisAngle` model value (the `value` on an
   * `{ field: "axisAngle", value }` edit) is degrees-native because it was
   * resolved from the NAMED form at import time. Mirrors, field-for-field,
   * cad-core's read-side discriminant `isArrayAxisAngleRadians`
   * (`packages/cad-core/src/common.ts`) built on
   * `ResolvedAssocArray.axisAngleFromNamedForm` /
   * `ArrayRectEntity.axisAngleFromNamedForm`
   * (`dxf-io/handleResolution.ts` / `drawing-model/dxfBridge.
   * arrayAssocImport.ts`): `true` when the graph's named "AxesAngle" value
   * param supplied the value (AW-1's units seam converted radians → degrees
   * at import, so the model's CURRENT value is genuinely DEGREES) — the
   * conversion below is correct and MUST run. `false` means the standalone
   * positional record supplied the value instead (never unit-converted at
   * import — "no fixture proves its axisAngle unit convention",
   * `axesAngleRadiansToModelDegrees`'s doc — so the model's CURRENT value is
   * still RADIANS) — converting it a SECOND time here on write would corrupt
   * the angle. In a HYBRID graph (both forms carry an axisAngle slot) the
   * positional form always wins the import-time merge
   * (`handleResolution.ts::mergeNamedRectValueParams`'s "standalone record
   * wins" rule), so `false` is the correct flag for that case even though a
   * named-form write target also exists here.
   *
   * `undefined` (the default) preserves the historical UNCONDITIONAL-convert
   * behavior — only an EXPLICIT `false` disables the conversion. This matters
   * for callers (e.g. this module's own low-level test fixtures) that build a
   * named-only-form scenario directly and never thread this flag at all; the
   * real production caller (`dxfBridge.arrayAssocExport.ts`'s
   * `applyAssocArrayWriteBack`) always threads an EXPLICIT boolean
   * (`entity.axisAngleFromNamedForm ?? false`) precisely so the hybrid
   * positional-wins case is never silently defaulted into converting.
   */
  axisAngleFromNamedForm?: boolean;
  /**
   * @codex P2 ("Track the owning action per named field") — per-field
   * ACDBASSOCACTION owner handle, mirrored verbatim from
   * `ResolvedAssocArray.ownerHandleByField` (handleResolution.ts) through
   * `entity.assocHandles.ownerHandleByField` (drawing-model). Present ONLY
   * when the import-side merge detected a genuine multi-action split — the
   * common case leaves this `undefined` and every field falls back to the
   * single `actionHandle` above, taking the exact pre-existing single-target
   * path. When present, the named-form write branch below resolves EACH
   * edited field's target action independently
   * (`ownerHandleByField[field] ?? actionHandle`) and patches every DISTINCT
   * resolved action record separately — a split array's edits land on the
   * record that genuinely owns each field instead of all being silently
   * routed to (or declined against) whichever action `actionHandle` bound to.
   */
  ownerHandleByField?: AssocArrayFieldOwnerMap;
}

export interface AssocArrayGraphPatchResult {
  /** The input `flatGroups`, with every successfully patched value replaced
   * in place (same array LENGTH — this module only ever overwrites an
   * EXISTING group's `value`, never inserts/removes groups). */
  groups: DxfGroup[];
  outcomes: AssocArrayFieldPatchOutcome[];
}

/**
 * Locate the `[start, end)` index range of the `0 <TYPE>` record whose FIRST
 * group-5 (self handle) matches `handle` (case-insensitive hex — DXF
 * convention, `normalizeHandle`). `end` is the index of the next `0`-coded
 * group, or `groups.length` for the last record in the stream. Mirrors the
 * record-boundary scan `replaceDictionaryChildEntries`
 * (`dxfBridge.mleaderStyleObjects.ts`) uses for the SAME "isolate one record
 * inside a flat verbatim stream" problem, generalized to match by handle
 * instead of by dictionary-entry name.
 */
function findRecordRangeByHandle(
  groups: readonly DxfGroup[],
  handle: string,
): { start: number; end: number } | undefined {
  const target = normalizeHandle(handle);
  if (target.length === 0) return undefined;
  let i = 0;
  while (i < groups.length) {
    if (groups[i]!.code !== 0) {
      i += 1;
      continue;
    }
    const start = i;
    let end = groups.length;
    for (let j = i + 1; j < groups.length; j += 1) {
      if (groups[j]!.code === 0) {
        end = j;
        break;
      }
    }
    for (let j = start + 1; j < end; j += 1) {
      if (groups[j]!.code === 5) {
        if (normalizeHandle(groups[j]!.value) === target) {
          return { start, end };
        }
        break; // first group-5 only — a LATER 5 inside the record body is not the self handle
      }
    }
    i = end;
  }
  return undefined;
}

/**
 * Rewrite ONE `ACDBASSOCACTION` record's group slice, patching the value
 * slot of every requested named param that decodes as a single literal
 * (non-formula) value with the expected group code. `namedEdits` is keyed by
 * the RAW param NAME ("Items" / "AxesAngle" / …) with the value ALREADY in
 * wire units (radians for AxesAngle — the caller applies
 * `axesAngleModelDegreesToRadians` before calling this).
 *
 * Grammar walked (mirrors `decodeAssocActionValueParams`'s read-side walk —
 * intentionally a SEPARATE, simpler rewrite pass rather than a shared
 * decode+patch walker: this function only needs to locate ONE value slot per
 * requested name, never assembles a decoded `AssocActionValueParam[]`, so
 * reusing the full decode-and-collect state machine would add index-tracking
 * plumbing to an already-tested, unrelated read path for no shared benefit):
 *
 *   100 AcDbAssocAction → … → 1 <name> → 90 <unitType> → 90 <numValues> → <value>
 *
 * Only the FIRST occurrence of a requested name is considered (mirrors
 * `mergeNamedRectValueParams`'s "first name wins"); the scan stops at a
 * later `100 <derived-subclass>` marker exactly like the decode function
 * (value params live in the AcDbAssocAction section only).
 */
function rewriteActionRecordGroups(
  recordGroups: readonly DxfGroup[],
  namedEdits: ReadonlyMap<string, { code: number; value: number }>,
): {
  groups: DxfGroup[];
  applied: Set<string>;
  skipped: Map<string, AssocArrayPatchSkipReason>;
} {
  const out: DxfGroup[] = recordGroups.map((g) => ({ ...g }));
  const applied = new Set<string>();
  const skipped = new Map<string, AssocArrayPatchSkipReason>();
  const remaining = new Set(namedEdits.keys());

  let pastActionMarker = false;
  let i = 0;
  while (i < out.length && remaining.size > 0) {
    const g = out[i]!;
    if (g.code === 100) {
      const m = g.value.trim();
      if (m === "AcDbAssocAction") {
        pastActionMarker = true;
      } else if (pastActionMarker) {
        break; // a derived subclass section begins — stop (matches decode)
      }
      i += 1;
      continue;
    }
    if (!pastActionMarker || g.code !== 1) {
      i += 1;
      continue;
    }
    const name = g.value;
    if (!remaining.has(name)) {
      i += 1;
      continue;
    }
    const edit = namedEdits.get(name)!;
    const unitG = out[i + 1];
    const countG = out[i + 2];
    if (
      unitG === undefined ||
      countG === undefined ||
      unitG.code !== 90 ||
      countG.code !== 90
    ) {
      skipped.set(name, "unexpected-code");
      remaining.delete(name);
      i += 1;
      continue;
    }
    // @codex P3 (local review, round-3) — use the SAME strict-integer
    // grammar the read-side decoder (`decodeAssocActionValueParams`,
    // assocArrayParser.ts) parses `numValues` with, not a narrower
    // string-literal regex. A source producer may legitimately emit a
    // valid DXF integer with a leading zero ("01") for numValues=1; the
    // read side already accepts it as a single literal — the write-side
    // guard must agree, or a genuinely patchable field gets wrongly
    // classified as expression-guarded and its edit silently fails to
    // persist.
    if (parseStrictInt(countG.value) !== 1) {
      // numValues !== 1 — a formula chain (or a malformed/oversized count).
      // Either way this is not a single literal value slot we may overwrite.
      skipped.set(name, "expression-guarded");
      remaining.delete(name);
      i += 1;
      continue;
    }
    const valueG = out[i + 3];
    if (valueG === undefined || valueG.code !== edit.code) {
      skipped.set(name, "unexpected-code");
      remaining.delete(name);
      i += 1;
      continue;
    }
    // @codex P2 (fresh review) — "Skip non-literal value params before
    // patching". The documented VALUEPARAM grammar (types.ts
    // `AssocActionValueParam` doc comment: "per value: the AcDbEvalVariant
    // group (40 real / 90 int32) + 330 input-variable id (ACDBASSOCVALUE
    // DEPENDENCY FK; "0" when literal)") puts a PER-VALUE 330 group
    // immediately after the value itself — even a single-EvalVariant block
    // (`numValues === 1`, already confirmed above) can still be
    // expression-driven via this dependency id rather than the multi-value
    // "formula chain" shape the guard above catches. Overwriting it would
    // silently sever that dependency instead of declining like the sibling
    // guard does. Read the raw handle string directly (same convention as
    // `writer.ts`'s `isDefaultLayer/g.code === 330 && ... return g.value ===
    // "0"` null-reference checks) rather than `parseStrictInt` — 330 handles
    // are hex strings ("1F", "2A3", …) that a decimal-only strict-int parse
    // would silently mis-classify as "not-a-number" for any handle
    // containing a letter, defeating the very guard being added. A MISSING
    // 330 group is treated as literal (preserve-source-never-infer — no
    // evidence of a dependency means none is assumed); every fixture in this
    // repo (`assocArrayPatch.test.ts` + `arrayAssoc.test.ts`) already emits
    // an explicit "330 0" after a literal value, so this default never masks
    // a real dependency in a fixture this codebase has ever observed.
    const depG = out[i + 4];
    if (depG !== undefined && depG.code === 330 && depG.value.trim() !== "0") {
      skipped.set(name, "expression-guarded");
      remaining.delete(name);
      i += 1;
      continue;
    }
    out[i + 3] = { code: edit.code, value: formatDxfNumber(edit.value) };
    applied.add(name);
    remaining.delete(name);
    i += 1;
  }

  for (const name of remaining) skipped.set(name, "missing");
  return { groups: out, applied, skipped };
}

/** Canonical positional order of the standalone
 * `ACDBASSOCARRAYRECTANGULARPARAMETERS` record's group-90 ints — mirrors
 * `decodeRectParams` (assocArrayParser.ts) and
 * `assocArrayWriter.ts#appendRectParams`. */
const RECT_PARAMS_INT_FIELD_ORDER: readonly AssocArrayRectFieldName[] = [
  "columnCount",
  "rowCount",
  "levelCount",
];
/** Canonical positional order of the record's group-40 doubles — same
 * sources as {@link RECT_PARAMS_INT_FIELD_ORDER}. */
const RECT_PARAMS_DOUBLE_FIELD_ORDER: readonly AssocArrayRectFieldName[] = [
  "columnSpacing",
  "rowSpacing",
  "levelSpacing",
  "axisAngle",
  "rowElevation",
];

/**
 * Rewrite ONE standalone `ACDBASSOCARRAYRECTANGULARPARAMETERS` record's
 * group slice, patching the Nth group-90 / group-40 value (per the canonical
 * positional order above) for every requested field that has a slot ALREADY
 * present in the record. A field whose positional slot the producer never
 * emitted (a shorter positional array — e.g. no `rowElevation`) is left
 * `missing`: this module NEVER appends a new group (preserve-source-never-
 * infer — a value with no persisted slot cannot be "corrected" without
 * inventing structure the source never had).
 *
 * @codex P2 (removed-behavior audit) — expression guard. The NAMED form
 * (`rewriteActionRecordGroups`) can tell a formula-chain value apart from a
 * single literal because each `ValueParam` carries its own explicit
 * "unit + count" preamble (`numValues !== 1`). The POSITIONAL form has no
 * such per-slot marker — `decodeRectParams` (assocArrayParser.ts) DOES
 * decode a group-1 "expression" text within this SAME record (feeding
 * `entity.columnCountExpression` etc. — `dxfBridge.arrayAssocImport.ts`),
 * but with no positional attribution linking a specific expression string to
 * a specific group-90/40 slot ordinal. Rather than guess that attribution
 * (preserve-source-never-infer), this function treats the PRESENCE of ANY
 * group-1 expression anywhere in the record's params-marker scope as reason
 * to skip EVERY requested field in this record with `"expression-guarded"` —
 * coarser than the named form's per-field guard, but never risks silently
 * overwriting a literal slot that a sibling expression actually governs.
 */
function rewriteRectParamsRecordGroups(
  recordGroups: readonly DxfGroup[],
  positionalEdits: ReadonlyMap<AssocArrayRectFieldName, number>,
): {
  groups: DxfGroup[];
  applied: Set<AssocArrayRectFieldName>;
  skipped: Map<AssocArrayRectFieldName, AssocArrayPatchSkipReason>;
} {
  const out: DxfGroup[] = recordGroups.map((g) => ({ ...g }));
  const applied = new Set<AssocArrayRectFieldName>();
  let pastMarker = false;
  let intOrdinal = 0;
  let doubleOrdinal = 0;
  let hasExpression = false;

  for (const g of out) {
    if (g.code === 100) {
      const m = g.value.trim();
      if (
        m === "AcDbAssocArrayRectangularParameters" ||
        m === "AcDbAssocArrayParameters"
      ) {
        pastMarker = true;
      }
      continue;
    }
    if (pastMarker && g.code === 1) {
      hasExpression = true;
      break;
    }
  }
  if (hasExpression) {
    const skipped = new Map<AssocArrayRectFieldName, AssocArrayPatchSkipReason>();
    for (const field of positionalEdits.keys()) skipped.set(field, "expression-guarded");
    return { groups: out, applied, skipped };
  }
  pastMarker = false;

  for (let i = 0; i < out.length; i += 1) {
    const g = out[i]!;
    if (g.code === 100) {
      const m = g.value.trim();
      if (
        m === "AcDbAssocArrayRectangularParameters" ||
        m === "AcDbAssocArrayParameters"
      ) {
        pastMarker = true;
      }
      continue;
    }
    if (!pastMarker) continue;
    if (g.code === 90) {
      const field = RECT_PARAMS_INT_FIELD_ORDER[intOrdinal];
      if (field !== undefined && positionalEdits.has(field)) {
        out[i] = { code: 90, value: formatDxfNumber(positionalEdits.get(field)!) };
        applied.add(field);
      }
      intOrdinal += 1;
      continue;
    }
    if (g.code === 40) {
      const field = RECT_PARAMS_DOUBLE_FIELD_ORDER[doubleOrdinal];
      if (field !== undefined && positionalEdits.has(field)) {
        out[i] = { code: 40, value: formatDxfNumber(positionalEdits.get(field)!) };
        applied.add(field);
      }
      doubleOrdinal += 1;
      continue;
    }
  }

  const skipped = new Map<AssocArrayRectFieldName, AssocArrayPatchSkipReason>();
  for (const field of positionalEdits.keys()) {
    if (!applied.has(field)) skipped.set(field, "missing");
  }
  return { groups: out, applied, skipped };
}

/**
 * Top-level AW-1 write-back entry point: patch `edits` into WHICHEVER of the
 * two emission forms `target` names, inside `flatGroups` (the document-wide
 * verbatim OBJECTS stream — `document.rawObjectGroups`). Pure — returns a
 * NEW array; `flatGroups` is never mutated. Both forms are attempted
 * independently for every edit (a real graph normally carries only one, but
 * AW-1's mandate is "patch BOTH forms of emission" — docs/MASTERPLAN.md
 * AW-1 — so a graph that happens to carry both stays consistent).
 */
export function patchAssocArrayGraphRawGroups(
  flatGroups: readonly DxfGroup[],
  target: AssocArrayGraphPatchTarget,
  edits: readonly AssocArrayFieldEdit[],
): AssocArrayGraphPatchResult {
  if (edits.length === 0) {
    return { groups: flatGroups.map((g) => ({ ...g })), outcomes: [] };
  }

  let groups = flatGroups.map((g) => ({ ...g }));
  const outcomeByField = new Map<AssocArrayRectFieldName, AssocArrayFieldPatchOutcome>();
  for (const edit of edits) {
    outcomeByField.set(edit.field, {
      field: edit.field,
      namedFormApplied: false,
      positionalFormApplied: false,
    });
  }

  // ── Named form — ACDBASSOCACTION value params (real AutoCAD shape) ──
  // @codex P2 ("Track the owning action per named field") — group the
  // requested edits by their RESOLVED owning action handle: the per-field
  // override in `target.ownerHandleByField` when present, else the array's
  // single fallback `target.actionHandle`. Each DISTINCT resolved handle's
  // record is then located and patched independently. A single-action array
  // (`target.ownerHandleByField` absent — the overwhelming common case, see
  // its doc) resolves EVERY edit to the same one handle, producing exactly
  // one group and taking the identical single-pass path this replaced.
  const editsByActionHandle = new Map<string, AssocArrayFieldEdit[]>();
  for (const edit of edits) {
    const handle = target.ownerHandleByField?.[edit.field] ?? target.actionHandle;
    if (!handle) {
      outcomeByField.get(edit.field)!.namedFormSkipReason = "missing";
      continue;
    }
    const list = editsByActionHandle.get(handle);
    if (list) list.push(edit);
    else editsByActionHandle.set(handle, [edit]);
  }
  for (const [actionHandle, handleEdits] of editsByActionHandle) {
    const range = findRecordRangeByHandle(groups, actionHandle);
    if (!range) {
      for (const edit of handleEdits) {
        outcomeByField.get(edit.field)!.namedFormSkipReason = "missing";
      }
      continue;
    }
    const namedEdits = new Map<string, { code: number; value: number }>();
    const fieldByName = new Map<string, AssocArrayRectFieldName>();
    for (const edit of handleEdits) {
      const entry = RECT_NAMED_VALUE_PARAMS.find((e) => e.field === edit.field);
      if (!entry) continue;
      // @codex P2 ("Preserve positional-axis units in hybrid arrays") —
      // only convert degrees → radians when the model's CURRENT axisAngle
      // value is actually degrees-native (see `AssocArrayGraphPatchTarget
      // .axisAngleFromNamedForm`'s doc for the full 3-way unit split this
      // mirrors from cad-core's `isArrayAxisAngleRadians`). An EXPLICIT
      // `false` (the hybrid-graph, positional-form-won case) means the
      // value is already RADIANS — converting it again here would corrupt
      // it. `undefined` (no caller opinion) preserves the historical
      // unconditional-convert default.
      const wireValue =
        edit.field === "axisAngle" && target.axisAngleFromNamedForm !== false
          ? axesAngleModelDegreesToRadians(edit.value)
          : edit.value;
      namedEdits.set(entry.name, { code: entry.code, value: wireValue });
      fieldByName.set(entry.name, edit.field);
    }
    const record = groups.slice(range.start, range.end);
    const rewritten = rewriteActionRecordGroups(record, namedEdits);
    groups = [
      ...groups.slice(0, range.start),
      ...rewritten.groups,
      ...groups.slice(range.end),
    ];
    for (const name of rewritten.applied) {
      const field = fieldByName.get(name);
      if (field) outcomeByField.get(field)!.namedFormApplied = true;
    }
    for (const [name, reason] of rewritten.skipped) {
      const field = fieldByName.get(name);
      if (field) outcomeByField.get(field)!.namedFormSkipReason = reason;
    }
  }

  // ── Positional form — standalone params record (synthetic shape) ──
  // `edit.value` is written UNCONVERTED here (no `axesAngleModelDegreesToRadians`,
  // unlike the named form above) — this is DELIBERATE symmetry with the READ
  // side: `handleResolution.ts::mergeNamedRectValueParams`'s AW-1 units-seam
  // doc explains the conversion is applied ONLY to the named form because "no
  // fixture proves [the positional form's] axisAngle unit convention" (a
  // synthetic/ODA-sample shape real AutoCAD never emits). Converting on write
  // while NOT converting on read would make an untouched round-trip of a
  // positional-only axisAngle silently DRIFT by the conversion factor — worse
  // than today's residual, pre-existing, already-documented ambiguity for the
  // (rare) case where a graph carries BOTH forms for the same value.
  if (target.arrayParametersHandle) {
    const range = findRecordRangeByHandle(groups, target.arrayParametersHandle);
    if (range) {
      const positionalEdits = new Map<AssocArrayRectFieldName, number>();
      for (const edit of edits) positionalEdits.set(edit.field, edit.value);
      const record = groups.slice(range.start, range.end);
      const rewritten = rewriteRectParamsRecordGroups(record, positionalEdits);
      groups = [
        ...groups.slice(0, range.start),
        ...rewritten.groups,
        ...groups.slice(range.end),
      ];
      for (const field of rewritten.applied) {
        outcomeByField.get(field)!.positionalFormApplied = true;
      }
      for (const [field, reason] of rewritten.skipped) {
        outcomeByField.get(field)!.positionalFormSkipReason = reason;
      }
    } else {
      for (const edit of edits) {
        outcomeByField.get(edit.field)!.positionalFormSkipReason = "missing";
      }
    }
  } else {
    for (const edit of edits) {
      outcomeByField.get(edit.field)!.positionalFormSkipReason = "missing";
    }
  }

  return { groups, outcomes: [...outcomeByField.values()] };
}
